import { ConsentManager } from '../../../consentService/ConsentManager';
import createScript, {
  addScriptErrorHandling,
  createInlineScript,
  scriptExists,
  insertScript,
  addPreconnectLink
} from '../../../../utils/createScript';
import IABTestingClient from '../../IABTestingClient';
import { DynamicYieldInputType } from './types';
import { ConsentUpdateEventData } from '../../../consentService/strategies';
import { internalLogger } from '../../../../interface/v1/clientOsLogger';
import { EVENT_CHANNELS } from '../../../consentService/strategies/BaseConsentStrategy';
import { getServices } from '../../../../infra/startup';
import { IEventService } from '../../../eventService';

/**
 * DynamicYieldClient - Manages Dynamic Yield A/B testing integration
 *
 * This client handles the initialization and lifecycle of Dynamic Yield scripts
 * based on user consent status.
 */
class DynamicYieldClient implements IABTestingClient {
  private readonly snippetId: string;
  private readonly scriptId: string = 'dynamicYield-script';
  private eventService: IEventService | null = null;
  private isInitialized: boolean = false;
  private isScriptsInjected: boolean = false;

  /**
   * Dynamic Yield CDN endpoints for preconnect optimization
   */
  private readonly preconnectUrls = [
    '//cdn.dynamicyield.com',
    '//st.dynamicyield.com',
    '//rcom.dynamicyield.com'
  ] as const;

  constructor(dynamicYield: DynamicYieldInputType) {
    this.snippetId = dynamicYield.snippetId;

    if (!this.snippetId) {
      throw new Error('DynamicYield snippetId is required');
    }
  }

  /**
   * Initializes the Dynamic Yield client
   *
   * This method:
   * 1. Checks if scripts are already present
   * 2. Injects scripts if user doesn't have the consented
   * 3. Sets up event listeners for consent changes
   *
   * Note: Dynamic Yield should always be initialized, even when user consents are not present,
   * to ensure proper event subscription for consent updates.
   */
  public init(): void {
    if (this.isInitialized) {
      internalLogger.warn('DynamicYieldClient already initialized');
      return;
    }

    try {
      // Check if scripts need to be injected based on current consent status
      this.handleScriptInjection();

      // Subscribe to consent update events to handle future consent changes
      this.setupConsentEventSubscription();

      this.isInitialized = true;
      internalLogger.log('DynamicYieldClient initialization completed');
    } catch (error) {
      internalLogger.error('Failed to initialize DynamicYieldClient', error);
      throw error;
    }
  }

  /**
   * Handles the initial script injection based on current consent status
   */
  private handleScriptInjection(): void {
    // Only inject scripts if they don't exist and user has consented
    if (
      !scriptExists(this.scriptId) &&
      !this.isScriptsInjected &&
      ConsentManager.hasUserConsented()
    ) {
      internalLogger.debug(
        'User has consented. Injecting Dynamic Yield scripts.'
      );
      this.injectDynamicYieldResources();
    }
  }

  /**
   * Sets up event subscription for consent updates
   */
  private setupConsentEventSubscription(): void {
    try {
      this.eventService = getServices().eventService;

      if (!this.eventService) {
        throw new Error('Event service not available');
      }

      void this.eventService.subscribe(
        EVENT_CHANNELS.CONSENT_UPDATED,
        this.handleConsentUpdateEvent.bind(this)
      );

      internalLogger.debug(
        'Dynamic Yield: Subscribed to consent update events'
      );
    } catch (error) {
      internalLogger.error(
        'Dynamic Yield: Failed to setup consent event subscription',
        error
      );
      throw error;
    }
  }

  /**
   * Handles consent update events
   *
   * When consent status changes, this method determines whether to:
   * - Inject scripts (if consent is granted and scripts not yet injected)
   * - Remove scripts (if consent is revoked - though Dynamic Yield doesn't typically support this)
   */
  private async handleConsentUpdateEvent(
    eventData: ConsentUpdateEventData
  ): Promise<void> {
    try {
      internalLogger.debug('Consent updated event received: ', eventData);

      // Check if scripts need to be injected based on current consent status
      this.handleScriptInjection();
    } catch (error) {
      internalLogger.error('Error handling consent update event', error);
    }
  }

  /**
   * Injects all Dynamic Yield resources (preconnect links + scripts)
   */
  private injectDynamicYieldResources(): void {
    try {
      // Add preconnect links for performance optimization
      this.addPreconnectLinks();

      // Inject the actual Dynamic Yield scripts
      this.insertDynamicYieldScripts();

      this.isScriptsInjected = true;
    } catch (error) {
      internalLogger.error('Failed to inject Dynamic Yield resources', error);
      throw error;
    }
  }

  /**
   * Adds preconnect links to optimize loading of Dynamic Yield resources
   *
   * Preconnect hints allow the browser to establish early connections
   * to the Dynamic Yield CDN, improving script loading performance.
   */
  private addPreconnectLinks(): void {
    this.preconnectUrls.forEach((url) => {
      try {
        const added = addPreconnectLink(url);
        if (added) {
          internalLogger.debug(`Added preconnect link for ${url}`);
        }
      } catch (error) {
        internalLogger.warn(`Failed to add preconnect link for ${url}`, error);
      }
    });
  }

  /**
   * Inserts Dynamic Yield scripts into the document head
   *
   * Dynamic Yield requires three components:
   * 1. Inline initialization script - Sets up DY object and recommendation context
   * 2. Dynamic script (api_dynamic.js) - Contains dynamic functionality
   * 3. Static script (api_static.js) - Contains static configuration
   *
   * Scripts are prepended to the head for faster execution.
   */
  private insertDynamicYieldScripts(): void {
    const dynamicScriptUrl = `https://cdn.dynamicyield.com/api/${this.snippetId}/api_dynamic.js`;
    const staticScriptUrl = `https://cdn.dynamicyield.com/api/${this.snippetId}/api_static.js`;

    try {
      // 1. Create and inject the inline initialization script first
      this.injectInitializationScript();

      // 2. Create external script elements
      const dynamicScript = createScript(
        `${this.scriptId}-dynamic`,
        dynamicScriptUrl
      );
      const staticScript = createScript(this.scriptId, staticScriptUrl);

      // Add error handling to scripts
      addScriptErrorHandling(dynamicScript, 'Dynamic Yield dynamic');
      addScriptErrorHandling(staticScript, 'Dynamic Yield static');

      // 3. Insert external scripts into document head
      insertScript(staticScript, 'append');
      insertScript(dynamicScript, 'append');
    } catch (error) {
      internalLogger.error('Failed to insert Dynamic Yield scripts', error);
      throw error;
    }
  }

  /**
   * Injects the inline initialization script that sets up Dynamic Yield context
   *
   * This script:
   * 1. Initializes the window.DY object if it doesn't exist
   * 2. Sets up the recommendation context (defaulting to HOMEPAGE)
   */
  private injectInitializationScript(): void {
    try {
      const initScriptId = `${this.scriptId}-init`;

      // Check if initialization script already exists
      if (scriptExists(initScriptId)) {
        internalLogger.debug(
          'Dynamic Yield initialization script already exists'
        );
        return;
      }

      // Create and insert inline script
      // TODO: adding as true because we already checked the state before
      const initScript = createInlineScript(
        initScriptId,
        `
        window.DY = window.DY || {};
        DY.userActiveConsent = {
           accepted: true
        };
      `
      );

      insertScript(initScript, 'prepend');

      internalLogger.debug(
        'Dynamic Yield userActiveConsent configuration injected'
      );
    } catch (error) {
      internalLogger.error(
        'Failed to inject Dynamic Yield userActiveConsent configuration',
        error
      );
      throw error;
    }
  }

  private hasUserConsented(): boolean {
    return ConsentManager.hasUserConsented();
  }

  /**
   * Cleanup method to unsubscribe from events and remove resources
   * Call this when the client is no longer needed
   */
  public destroy(): void {
    try {
      // Unsubscribe from consent events
      if (this.eventService) {
        void this.eventService.removeEventListener(
          EVENT_CHANNELS.CONSENT_UPDATED,
          this.handleConsentUpdateEvent.bind(this)
        );
        this.eventService = null;
      }

      this.isInitialized = false;
      internalLogger.log('DynamicYieldClient destroyed');
    } catch (error) {
      internalLogger.error(
        'Error during DynamicYieldClient destruction',
        error
      );
    }
  }
}

export default DynamicYieldClient;
